<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useColorMode } from '@vueuse/core'
import {
  refreshSupaSession,
  supabase,
} from '@/lib/supabaseClient'
import AgentBubble from '@/components/agent/AgentBubble.vue'
import NewMeetModal from '@/components/videoconf/NewMeetModal.vue'
import PlanSelectionModal from '@/components/PlanSelectionModal.vue'
import PaymentSuccessModal from '@/components/PaymentSuccessModal.vue'
import { useMeetingStore } from '@/store/meetingStore' // HARDCODE the mode. Remove this line to use the system preference

import { usePageTitle } from '@/lib/utils/vueUtils'

const publicRoutes = [
  'home',
  'login',
  'signup',
  'startActivity',
  'realtimeActivity',
  'formActivity',

  'chatActivity',
  'activity',
  'faq',
  'landing',
  'privacy',
  'terms',
  'debuterInvite',
  'PatientSignupDone',
  'meetRoom',
  'convo',
  'styleguide',
  'realtime',
  'beta',
  'standaloneExtra',
  'meet',
  'meet1',
  'blogArticle',
  'blogListing',
  '404',
  'NotFound',
  'landing_guru',
  'landing_en',
  'meetDone',
]

const noSidebarRoute = [
  'meet',
  'meet1',
  'meetRoom',
  'host-room',
  // "convo",
  'standaloneExtra',
  'formActivity',
  'realtimeActivity',
  'beta',
  'realtime',
  'styleguide',
  'PatientSignupDone',
  'debuterInvite',
  'privacy',
  'terms',
  'faq',
  'activity',
  'startActivity',
  'landing',
  'landing_guru',
  'settings',
  // "help",

  // admins
  'admin',
  'NotFoundAdmin',
  'AdminMedias',
]

const authRoutes = ['login', 'signup']
const ALLOW_LOCALHOST_INCOGNINTO = true

const route = useRoute()
const router = useRouter()
const mode = useColorMode({
  emitAuto: true,
  disableTransition: false,
})
mode.value = 'light'

const BRAND = 'Selfwise'

// Default title from route meta
const routeTitle = computed(() => route.meta.title)

// Create global title with consistent format using our utility
usePageTitle({
  title: routeTitle,
  brand: BRAND,
  titleFirst: true,
})

const session = ref()
const loading = ref(true)
const initialized = ref(false)
const routeReady = ref(false)

// Compute if current route is public
const isPublicRoute = computed(() => {
  // For routes with names, check against the publicRoutes list
  if (route.name) {
    return publicRoutes.includes(route.name)
  }

  // For routes without names, consider certain paths as public
  // Add specific path patterns here as needed
  const publicPaths = ['/dashboard', '/landing_mar12']
  return publicPaths.includes(route.path)
})

// Compute if current route is auth route
const isAuthRoute = computed(() => {
  return authRoutes.includes(route.name)
})

// Set routeReady when we have a valid route object
watch(
  () => route.path, // we assume even the root route has a path...
  (newPath) => {
    if (newPath) {
      routeReady.value = true
    }
  },
  { immediate: true },
)

// Determine if auth page should be shown
const shouldShowAuthPage = computed(() => {
  if (loading.value || !initialized.value || !routeReady.value)
    return false

  return (
    !session.value
    && !isPublicRoute.value
    && !(location.hostname === 'localhost' && ALLOW_LOCALHOST_INCOGNINTO)
  )
})

// Watch for auth state changes and redirect if needed
watch([() => session.value, () => route.name], ([newSession, routeName]) => {
  if (!initialized.value)
    return

  // Redirect logged-in users away from auth routes
  if (newSession && isAuthRoute.value) {
    router.replace({ name: 'home' })
  }
})

// Get the meeting store
const meetingStore = useMeetingStore()

// Get patients from the store
const patients = computed(() => meetingStore.patients)

// Helper function to initialize all store data
async function initStoreData() {
  // Prevent multiple simultaneous init calls
  if (meetingStore.patientsLoading || meetingStore.meetsLoading) {
    console.log('Store data already initializing, skipping...')
    return
  }

  try {
    await Promise.all([
      meetingStore.fetchPatients(),
      meetingStore.fetchMeetings(),
    ])
    console.log('Store data initialized')
  }
  catch (error) {
    console.error('Failed to initialize store data:', error)
  }
}

onMounted(async () => {
  try {
    const { data } = await supabase.auth.getSession()
    const res = await refreshSupaSession(1)
    session.value = data.session

    if (session.value) {
      // Initialize store data for authenticated users
      await initStoreData()

      // Set current user in the meeting store
      if (session.value?.user) {
        meetingStore.setCurrentUser({
          id: session.value.user.id,
          email: session.value.user.email,
          name:
            session.value.user.user_metadata?.full_name
            || session.value.user.email?.split('@')[0]
            || '',
        })
      }
    }
  }
  catch (error) {
    console.error('Error getting session:', error)
  }
  finally {
    loading.value = false
    initialized.value = true
  }

  supabase.auth.onAuthStateChange(async (event, _session) => {
    const previouslyLoggedIn = !!session.value
    session.value = _session

    // When user signs in, fetch data only if we haven't already initialized
    if (!previouslyLoggedIn && _session) {
      try {
        // Only init data if not already done in onMounted
        if (!initialized.value) {
          await initStoreData()
        }

        // Set current user in the meeting store
        if (_session?.user) {
          meetingStore.setCurrentUser({
            id: _session.user.id,
            email: _session.user.email,
            name:
              _session.user.user_metadata?.full_name
              || _session.user.email?.split('@')[0]
              || '',
          })
        }
      }
      catch (error) {
        console.error('Error fetching data after auth change:', error)
      }
    }
  })
})

/// compute show Sudebar // !noSidebarRoute.includes(route.name)
const showSidebar = computed(() => {
  // Access these properties to ensure reactivity
  const currentRoute = route.name
  const currentPath = route.path
  const currentSession = session.value

  return (
    currentSession
    && !currentPath.startsWith('/admin')
    && !noSidebarRoute.includes(currentRoute)
  )
})
</script>

<template>
  <div v-if="initialized && routeReady" class="md:block">
    <Toaster />
    <div class="border-t">
      <div class="bg-background">
        <!-- Show auth page if needed -->
        <Auth v-if="shouldShowAuthPage" />

        <!-- Show sidebar only for authenticated users -->
        <SidebarLayout
          v-if="session"
          :session="session"
          :show-sidebar="showSidebar"
          :patients="patients"
        >
          <router-view :session="session" :patients="patients" />
        </SidebarLayout>

        <!-- Show route content without sidebar for public routes -->
        <div v-if="!session && isPublicRoute" class="content-no-sidebar">
          <router-view :session="session" />
        </div>
      </div>
    </div>

    <!-- Add the AgentBubble component for conversations -->
    <AgentBubble v-if="session" class="hidden md:flex" />

    <!-- Include the NewMeetModal component for scheduling meetings -->
    <NewMeetModal v-if="session" />

    <!-- Include the PlanSelectionModal for plan upgrades -->
    <PlanSelectionModal v-if="session" :session="session" />

    <!-- Include the PaymentSuccessModal for successful payments -->
    <PaymentSuccessModal v-if="session" :session="session" />
  </div>
  <!-- Show nothing while initializing or waiting for route -->
  <div v-else class="min-h-screen bg-background" />
</template>

<style scoped>
.content-no-sidebar {
  min-height: calc(100vh - 4rem);
}
</style>
